/**
 * Editor preferences: a handful of switches, kept in localStorage beside the
 * theme. They describe how MyTXT shows text, never the text itself.
 */

export interface EditorPrefs {
  /** Line numbers in the margin. Off by default: MyTXT is for prose first. */
  lineNumbers: boolean;
  /** A faint band behind the line with the caret. */
  currentLine: boolean;
  /**
   * Tab indents (Shift-Tab outdents) the selected lines. Off, Tab and
   * Shift-Tab move keyboard focus like anywhere else on the page.
   */
  tabIndent: boolean;
  /** New and closed windows re-arrange the canvas. Off: windows stay where they are put. */
  autoArrange: boolean;
  /**
   * Upload edits to the cloud on their own while signed in. Off, changes wait
   * for "Sync Now". Never affects local draft recovery, which is always on.
   */
  cloudAutosave: boolean;
}

export const DEFAULT_PREFS: EditorPrefs = { lineNumbers: false, currentLine: true, tabIndent: true, autoArrange: false, cloudAutosave: true };

export const PREFS_KEY = 'mytxt.editor';

export function parsePrefs(raw: unknown): EditorPrefs {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      value = null;
    }
  }
  const v = (value && typeof value === 'object' ? value : {}) as Partial<Record<keyof EditorPrefs, unknown>>;
  return {
    lineNumbers: typeof v.lineNumbers === 'boolean' ? v.lineNumbers : DEFAULT_PREFS.lineNumbers,
    currentLine: typeof v.currentLine === 'boolean' ? v.currentLine : DEFAULT_PREFS.currentLine,
    tabIndent: typeof v.tabIndent === 'boolean' ? v.tabIndent : DEFAULT_PREFS.tabIndent,
    autoArrange: typeof v.autoArrange === 'boolean' ? v.autoArrange : DEFAULT_PREFS.autoArrange,
    cloudAutosave: typeof v.cloudAutosave === 'boolean' ? v.cloudAutosave : DEFAULT_PREFS.cloudAutosave,
  };
}

export function loadPrefs(storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): EditorPrefs {
  try {
    return parsePrefs(storage?.getItem(PREFS_KEY));
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: EditorPrefs, storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // A preference that cannot be remembered is still applied for this visit.
  }
}

/**
 * Remove MyTXT's own keys from localStorage. Only keys MyTXT owns (`mytxt.*`);
 * preferences (theme, editor switches) only when asked.
 */
export function clearLocalKeys(includePrefs: boolean, storage: Storage | undefined = globalThis.localStorage): string[] {
  const removed: string[] = [];
  try {
    if (!storage) return removed;
    const keep = includePrefs ? new Set<string>() : new Set([PREFS_KEY, 'mytxt.theme']);
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith('mytxt.') && !keep.has(k)) keys.push(k);
    }
    for (const k of keys) {
      storage.removeItem(k);
      removed.push(k);
    }
  } catch {
    // Storage that cannot be read holds nothing to clear.
  }
  return removed;
}
